import get from 'lodash.get';
import isObject from 'lodash.isobject';
import isArray from 'lodash.isarray';
import forEach from 'lodash.foreach';
import map from 'lodash.map';
import some from 'lodash.some';
import isBoolean from 'lodash.isboolean';
import { canSellDealershipTypes } from '@/constants/dealershipTypes';
import Dealership from '@/models/Dealership';
import { isUndefinedOrNullOrEmpty } from '@/utilities';

export default class User {
  constructor(user) {
    if (isUndefinedOrNullOrEmpty(user)) {
      return;
    }

    Object.assign(this, user);
    this.member_type = 'rep';
    // Set default. Override if we detect the appropriate dealerships/VCI type below -ETC
    this.canSell = false;

    // If a user has no assigned dealerships, then they never completed registration.
    // Set them as an admin so they can go back and finish it. - ETC
    // Refresh users returns a single dealer index with no props, so need to check the first dealer
    // actually has data -
    if ((isObject(this.dealerships) && !isArray(this.dealerships)
      && Object.keys(this.dealerships).length > 0)
      || (isArray(this.dealerships) && Object.keys(get(this, 'dealerships[0]', {})).length > 0)) {
      this.updateAssignedDealerships(this.dealerships);
    } else {
      this.member_type = 'admin';
    }
  }

  updateAssignedDealerships(updatedDealerships) {
    this.dealerships = {};
    this.activeDealerships = {};
    this.defaultDealer = null;
    this.hasActiveDealer = false;
    forEach(updatedDealerships, (dealership) => {
      const dealershipInstance = new Dealership(dealership);
      this.dealerships[dealershipInstance.id] = dealershipInstance;

      if (!this.defaultDealer) {
        this.defaultDealer = dealershipInstance;
      }

      if (dealershipInstance.memberType === 'admin' // expanding this check for backoffice just in case
        || dealershipInstance.memberType === 'backoffice') {
        this.member_type = dealershipInstance.memberType;
      }
      // 1  franchise
      // 2  independent
      // 3  wholesaler
      // 4  certified acv
      // 5  commercial
      const dealershipType = Number.parseInt(dealershipInstance.dealershipType, 10);
      if (canSellDealershipTypes.includes(dealershipType)
        || this.is_vci) {
        this.canSell = true;
      }

      // Check for active dealer
      if (dealershipInstance.active) {
        this.hasActiveDealer = true;
        this.activeDealerships[dealershipInstance.id] = dealershipInstance;

        if (!this.defaultDealer || !this.defaultDealer.active) {
          this.defaultDealer = dealershipInstance;
        }
      }
    });
  }

  isMemberOfSellingDealership(sellerDealerId) {
    return Object.prototype.hasOwnProperty.call(this.dealerships || {}, sellerDealerId);
  }

  isMemberOfWinningDealership(buyerDealerId) {
    return Object.prototype.hasOwnProperty.call(this.dealerships || {}, buyerDealerId);
  }

  isMemberOfDealership(dealerId) {
    return Object.prototype.hasOwnProperty.call(this.dealerships || {}, dealerId);
  }

  getReportAProblemDealerId(auction) {
    if (this.isMemberOfWinningDealership(auction.buyerDealerId)) {
      return auction.buyerDealerId;
    }
    if (this.isMemberOfSellingDealership(auction.sellerDealerId)) {
      return auction.sellerDealerId;
    }
    return null;
  }

  get id() {
    return Number.parseInt(get(this, 'user_id', 0), 10) || 0;
  }

  set id(id) {
    this.user_id = Number.parseInt(id, 10);
  }

  get zipCode() {
    return get(this, 'zip_code');
  }

  set zipCode(zipCode) {
    this.zip_code = zipCode;
  }

  get registrationState() {
    return Number.parseInt(get(this, 'registration_state', 0), 10);
  }

  get defaultDealerId() {
    return Number.parseInt(get(this.defaultDealer, 'id', 0), 10);
  }

  get hasMultipleDealerships() {
    return Object.keys(this.activeDealerships || {}).length > 1;
  }

  get assignedDealershipIds() {
    return map(this.dealerships, (d) => Number.parseInt(d.id, 10));
  }

  isWinningUser(auction) {
    return auction.highBidUserId === this.id;
  }

  isPrimarySeller(auction) {
    return this.id === auction.sellerUserId;
  }

  get isBackOfficeUser() {
    return some(this.dealerships, ['memberType', 'backoffice']);
  }

  get isAdmin() {
    return some(this.dealerships, ['memberType', 'admin']);
  }

  isAdminOfSellingDealership(dealerId) {
    return this.dealerships?.[dealerId]?.memberType === 'admin';
  }

  get dealershipName() {
    return get(this.defaultDealer, 'name');
  }

  get fullName() {
    return get(this, 'full_name');
  }

  set fullName(value) {
    this.full_name = value;
  }

  get userRole() {
    return get(this, 'role', 'user').toUpperCase();
  }

  get memberType() {
    return get(this, 'member_type', 'rep').toLowerCase();
  }

  get isActive() {
    return isBoolean(this.active) ? this.active : Number.parseInt(this.active, 10) === 1;
  }

  get inactiveMessage() {
    return get(this, 'inactive_message', {
      title: 'Your dealership is not currently active.',
      text: 'You will not be able to bid on auctions or take auction actions until your dealership has been activated. Please contact us at 1-800-552-4070 for resolution. If you need to finalize auctions in the interim, we can help.',
    });
  }

  get canPlaceHoldOnAuctions() {
    return this.dealershipsThatCanHoldAuctions.length > 0;
  }

  get dealershipsThatCanHoldAuctions() {
    return this.multibuyerDealerships || [];
  }

  get userCampaigns() {
    return this.campaigns;
  }

  get capitalUserData() {
    return get(this, 'capitalUser', {});
  }

  get capitalDealerSelectionList() {
    return get(this, 'capitalUser.dealer_selection_list', []);
  }

  get isBillingContactOfSomeDealership() {
    return Object.values(this.activeDealerships || {})
      .some((dealership) => dealership.billingContactLob?.length);
  }

  get appRoles() {
    return get(this, 'app_role', []);
  }
}
